/*
  Client-side wishlist - localStorage-backed, no app/backend required. Works
  identically for guests and logged-in customers on the same browser/device
  (Shopify has no native wishlist and adding one server-side would need a
  paid app or a customer-metafield write, which this theme's Admin API
  access isn't scoped for).

  Storage shape: an array of plain objects, one per wishlisted product,
  keyed by handle - {handle, title, url, image, price, comparePrice}. Storing
  the full card data (not just the handle) means the wishlist page can
  render immediately from localStorage alone, with no follow-up fetch per
  item.
*/
(function () {
  var STORAGE_KEY = 'tws_wishlist';

  function readWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // storage full/unavailable (e.g. private browsing) - wishlist just
      // doesn't persist this session, nothing else to do about it here.
    }
  }

  function isInWishlist(handle) {
    return readWishlist().some(function (item) {
      return item.handle === handle;
    });
  }

  function addToWishlist(item) {
    var items = readWishlist();
    if (!items.some(function (existing) { return existing.handle === item.handle; })) {
      items.push(item);
      writeWishlist(items);
    }
  }

  function removeFromWishlist(handle) {
    var items = readWishlist().filter(function (item) {
      return item.handle !== handle;
    });
    writeWishlist(items);
  }

  function updateBadge() {
    var badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    var count = readWishlist().length;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  function syncToggleState(button) {
    var handle = button.getAttribute('data-wishlist-handle');
    var inWishlist = isInWishlist(handle);
    button.classList.toggle('is-active', inWishlist);
    button.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      (inWishlist ? 'Remove ' : 'Add ') + button.getAttribute('data-wishlist-title') + (inWishlist ? ' from wishlist' : ' to wishlist')
    );
    var label = button.querySelector('[data-wishlist-label]');
    if (label) label.textContent = inWishlist ? 'In Wishlist' : 'Add to Wishlist';
  }

  function bindToggle(button) {
    syncToggleState(button);
    if (button.dataset.wishlistBound) return;
    button.dataset.wishlistBound = 'true';
    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      var handle = button.getAttribute('data-wishlist-handle');
      if (isInWishlist(handle)) {
        removeFromWishlist(handle);
      } else {
        addToWishlist({
          handle: handle,
          title: button.getAttribute('data-wishlist-title'),
          url: button.getAttribute('data-wishlist-url'),
          image: button.getAttribute('data-wishlist-image'),
          variantId: button.getAttribute('data-wishlist-variant-id'),
          price: button.getAttribute('data-wishlist-price'),
          comparePrice: button.getAttribute('data-wishlist-compare-price'),
        });
      }
      // Every card for the same product on the current page (e.g. it
      // could appear in two different carousels at once) stays in sync,
      // not just the one that was clicked.
      document.querySelectorAll('[data-wishlist-toggle][data-wishlist-handle="' + CSS.escape(handle) + '"]').forEach(syncToggleState);
      updateBadge();
      if (document.querySelector('[data-wishlist-page]')) renderWishlistPage();
    });
  }

  function initToggles(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(bindToggle);
  }

  // initToggles() above only runs once, on DOMContentLoaded, so a wishlist
  // button added afterward - the quick-view modal (theme.js's
  // _openQuickView sets modal.querySelector('.modal__inner').innerHTML
  // directly, with no event this file can hook into) or any other
  // AJAX-swapped markup - would otherwise never get its click handler bound
  // or its initial pressed/label state synced. Same MutationObserver
  // pattern layout/theme.liquid already uses for the cart-count badge and
  // stock-warning fixes, for the same class of problem.
  function watchForNewToggles() {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-wishlist-toggle]')) bindToggle(node);
          if (node.querySelectorAll) node.querySelectorAll('[data-wishlist-toggle]').forEach(bindToggle);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function moneyFormat(cents) {
    if (!cents) return '';
    var amount = (parseInt(cents, 10) / 100).toFixed(2);
    return 'Rs. ' + amount.replace(/\.00$/, '.00');
  }

  function renderWishlistPage() {
    var container = document.querySelector('[data-wishlist-page]');
    if (!container) return;
    var items = readWishlist();
    var emptyState = document.querySelector('[data-wishlist-empty]');

    if (items.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      return;
    }
    if (emptyState) emptyState.hidden = true;

    container.innerHTML = items
      .map(function (item) {
        var onSale = item.comparePrice && parseInt(item.comparePrice, 10) > parseInt(item.price, 10);
        var discount = onSale ? Math.round((1 - item.price / item.comparePrice) * 100) : 0;
        var handle = escapeHtml(item.handle);
        var title = escapeHtml(item.title);
        var url = escapeHtml(item.url);
        var image = escapeHtml(item.image);
        var variantId = escapeHtml(item.variantId);
        var price = escapeHtml(item.price);
        var comparePrice = escapeHtml(item.comparePrice);
        return (
          '<div class="product-card-wrapper">' +
            (onSale ? '<div class="product-card__sale-badge">Save ' + discount + '%</div>' : '') +
            '<button type="button" class="wishlist-toggle is-active" data-wishlist-toggle data-wishlist-handle="' + handle + '" data-wishlist-title="' + title + '" data-wishlist-url="' + url + '" data-wishlist-image="' + image + '" data-wishlist-variant-id="' + variantId + '" data-wishlist-price="' + price + '" data-wishlist-compare-price="' + comparePrice + '" aria-pressed="true" aria-label="Remove ' + title + ' from wishlist">' +
              '<svg class="wishlist-toggle__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>' +
            '</button>' +
            '<a href="' + url + '" class="product-card">' +
              '<div class="product-card__image-wrapper" style="aspect-ratio: 0.75;">' +
                (item.image ? '<img class="product-card__image" src="' + image + '" alt="' + title + '" loading="lazy">' : '') +
              '</div>' +
              '<div class="product-card__info">' +
                '<h3 class="product-card__title">' + title + '</h3>' +
                '<div class="product-card__price-wrapper">' +
                  '<span class="product-card__price">' + moneyFormat(item.price) + '</span>' +
                  (onSale ? '<span class="product-card__compare-price">' + moneyFormat(item.comparePrice) + '</span>' : '') +
                '</div>' +
              '</div>' +
            '</a>' +
            '<div class="product-card__buttons">' +
              '<button type="button" class="product-card__button product-card__button--primary" data-wishlist-add-to-cart data-variant-id="' + variantId + '">Add to cart</button>' +
              '<a href="' + url + '" class="product-card__button product-card__button--secondary">Quick view</a>' +
            '</div>' +
          '</div>'
        );
      })
      .join('');

    initToggles(container);
    initAddToCartButtons(container);
    validateWishlistAgainstCatalog(items);
  }

  /*
    Per explicit report ("all products deleted, why wishlist showing"):
    localStorage keeps whatever snapshot was true the moment a product was
    wishlisted, forever, with nothing to ever invalidate it - a product
    deleted/unpublished long ago still shows up with its old cached image/
    price and an Add to cart button that will always fail (the variant ID
    it's wired to no longer resolves to anything).

    Keeps the instant-render-from-localStorage behaviour the original
    design wanted (see the comment at the top of this file) - this runs
    AFTER that render, as a background correction pass, not instead of it.
    Checks each item's real product.js in parallel and drops any that 404
    (product genuinely gone) from both the page and localStorage itself,
    so it doesn't keep reappearing on a future visit. A network hiccup
    (fetch rejects rather than resolving not-ok) deliberately keeps the
    item rather than risk deleting it over a transient failure.
  */
  function validateWishlistAgainstCatalog(items) {
    if (!items.length) return;
    Promise.all(
      items.map(function (item) {
        return fetch('/products/' + encodeURIComponent(item.handle) + '.js', { credentials: 'same-origin' })
          .then(function (response) { return response.ok ? item : null; })
          .catch(function () { return item; });
      })
    ).then(function (results) {
      var stillValid = results.filter(Boolean);
      if (stillValid.length === items.length) return;
      writeWishlist(stillValid);
      updateBadge();
      renderWishlistPage();
    });
  }

  function initAddToCartButtons(root) {
    root.querySelectorAll('[data-wishlist-add-to-cart]').forEach(function (button) {
      if (button.dataset.wishlistAtcBound) return;
      button.dataset.wishlistAtcBound = 'true';
      button.addEventListener('click', function () {
        var variantId = button.getAttribute('data-variant-id');
        if (!variantId || variantId === 'null') return;
        var originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding...';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add_to_cart_failed');
            return fetch('/cart.js');
          })
          .then(function (response) { return response.json(); })
          .then(function (cart) {
            var cartCount = document.querySelector('.header__cart-count');
            if (cartCount) cartCount.textContent = cart.item_count;
            button.textContent = 'Added!';
            setTimeout(function () {
              button.textContent = originalText;
              button.disabled = false;
            }, 1500);
          })
          .catch(function () {
            button.textContent = 'Try again';
            button.disabled = false;
          });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initToggles(document);
    updateBadge();
    renderWishlistPage();
    watchForNewToggles();
  });

  // Keeps the badge (and, if open in another tab, the wishlist page) in
  // sync if the wishlist changes elsewhere.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    updateBadge();
    initToggles(document);
    renderWishlistPage();
  });
})();
